import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next/src';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import Toast from '@hobs/components/Toast';
import { GhostButton } from '@hobs/components/Buttons';
import File from '@hobs/components/File';
import { ACCEPTED_FILE_FORMATS, MAX_FILE_SIZE } from '@hobs/utils/constants';
import classNames from 'classnames';
import Styles from './DropZone.module.scss';
import Loader from '../Loader';
import { connect } from 'react-redux';
import { createLoadingSelector } from '@hobs/utils/loading';

const checkIfFileNameExists = (files, acceptedFiles) => {
  return files.reduce((result, item) => {
    if (result) {
      return result;
    }
    return acceptedFiles.find(aitem => aitem.name === item.name);
  }, false);
};

class DropZone extends PureComponent {
  state = {
    files: [],
    isUploadingFiles: false
  };

  componentDidMount() {
    this.setState({
      files: this.props.value
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        files: this.props.value
      });
    }
  }

  getNames() {
    const { disabled, t, onRemove, onChange, showSuccessMessages, isUploadingFiles } = this.props;
    const { files } = this.state;

    if (files.length === 0) {
      return t('No file attached');
    }

    return (
      <>
        {isUploadingFiles && (
          <div className={Styles['loader-backdrop']}>
            <div className={Styles['attachment-loader']}>
              <Loader />
            </div>
          </div>
        )}
        {files.map(file => {
          return (
            <File
              attachmentId={file.attachmentId}
              name={file.name}
              onRemove={() => {
                const list = files.reduce((result, item) => {
                  if (
                    file.attachmentId &&
                    file.attachmentId !== item.attachmentId
                  ) {
                    result.push(item);
                  } else if (
                    item.name !== file.name &&
                    item.path !== file.path
                  ) {
                    result.push(item);
                  }
                  return result;
                }, []);

                this.setState(
                  {
                    files: list
                  },
                  () => {
                    onRemove(file, list);
                    onChange(list);
                  }
                );

                if (showSuccessMessages) {
                  Toast.success(t('File removed successfully'));
                }
              }}
              key={ShortId.generate()}
              disabled={
                disabled ||
                (file.hasOwnProperty('canRemove') && !file.canRemove)
              }
            />
          );
        })}
      </>
    );
  }

  onDrop = acceptedFiles => {
    const { multiple, t, onSelect, onChange, showSuccessMessages } = this.props;
    const { files } = this.state;

    if (acceptedFiles.length > 0) {
      if (!checkIfFileNameExists(files, acceptedFiles)) {
        let files = [];
        if (multiple === true) {
          files = [].concat(acceptedFiles, files);
        } else {
          files = [...acceptedFiles];
        }

        this.setState(
          {
            files
          },
          () => {
            onSelect(acceptedFiles);
            onChange(acceptedFiles);
          }
        );

        if (showSuccessMessages) {
          Toast.success(t('File uploaded successfully'));
        }
      } else {
        Toast.warning(t('File with the same name is already added'));
      }
    }
  };

  onDropRejected = files => {
    const { multiple, t, onError } = this.props;
    const inMb = MAX_FILE_SIZE / 1024 / 1024;
    const errorCodes = files[0].errors.map(error => error.code) || [];
    
    errorCodes.forEach(error => {
      if (error === 'file-invalid-type') {
        Toast.warning(t('Selected file format is not allowed'));
      }
      else if (error === 'file-too-large') {
        Toast.warning(t('Selected file is too large (max ' + inMb + 'mb)'));
      }
      else {
        Toast.warning(t('Bad file'));
      }
    });

    if (files.length > 1 && multiple === false) {
      onError(t('Multiple files not accepted'));
    } else {
      onError(t('Unaccepted file format!'));
    }
  };

  render() {
    const { accept, multiple, maxSize, disabled, t, isUploadingFiles } = this.props;

    let dropzoneParams = {
      onDrop: this.onDrop,
      onDropRejected: this.onDropRejected,
      multiple,
      maxSize
    };
    if (accept.length > 0) {
      dropzoneParams = { ...dropzoneParams, accept };
    }

    return (
      <div className={classNames(Styles['container'], 'stop-drag')}>
        <div>
          <div className={Styles['info']}>{this.getNames()}</div>
        </div>
        {!disabled && (
          <Dropzone {...dropzoneParams}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div {...getRootProps()} className={Styles['zone']}>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>{t('Drop the files here ...')}</p>
                ) : (
                  <p>{t('Drag & drop here or')}</p>
                )}
                <GhostButton disabled={isUploadingFiles}>
                  {multiple ? t('Select files') : t('Select file')}
                </GhostButton>
              </div>
            )}
          </Dropzone>
        )}
      </div>
    );
  }
}

DropZone.defaultProps = {
  value: [],
  onChange: () => {},
  onSelect: () => {},
  onRemove: () => {},
  onError: () => {},
  accept: ACCEPTED_FILE_FORMATS,
  multiple: true,
  maxSize: MAX_FILE_SIZE,
  disabled: false,
  showSuccessMessages: false,
  isUploadingFiles: false
};

DropZone.propTypes = {
  t: PropTypes.func,
  value: PropTypes.array,
  accept: PropTypes.array,
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  multiple: PropTypes.bool,
  maxSize: PropTypes.number,
  disabled: PropTypes.bool,
  showSuccessMessages: PropTypes.bool
};

const isUploadingFiles = createLoadingSelector([
  'Attachments/Upload',
  'ServiceRequests/UpdateGeneralAttachments'
]);


const mapStateToProps = state => ({
  isUploadingFiles: isUploadingFiles(state),
});

export default connect(
  mapStateToProps,
  null
)(withTranslation()((DropZone)));